import React from 'react';
import { Box, Button, Chip, Grid, Paper } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import InboxIcon from '@mui/icons-material/Inbox';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/page-header';
import { ClientGate } from '../components/client-gate';
import { useSerClient } from '../components/use-ser-client';
import { ACTION_TYPES, useSubmissionSendDispatch, useSubmissionSendState } from './context/submission-send-context';
import { useSubmissionSendController } from './context/submission-send-controller';
import { SendStep, validateDataStep, validateFilesStep, validateVehiclesStep } from './submission-send-rules';
import { SendStepper } from './components/send-stepper';
import { DataStep } from './components/data-step';
import { FilesStep } from './components/files-step';
import { VehiclesStep } from './components/vehicles-step';
import { ReviewStep } from './components/review-step';
import { DraftList } from './components/draft-list';
import { SubmissionSuccess } from './components/submission-success';

export function SubmissionSendComponent() {
    const navigate = useNavigate();
    const client = useSerClient();
    const state = useSubmissionSendState();
    const dispatch = useSubmissionSendDispatch();
    const ctrl = useSubmissionSendController(client.clientId);
    const draft = ctrl.activeDraft;
    const draftIndex = state.drafts.findIndex((d) => d.key === draft.key);
    const errors = state.showErrors;

    function renderStep() {
        switch (state.activeStep) {
            case SendStep.Data:
                return <DataStep draft={draft} errors={errors ? validateDataStep(draft) : {}} onChange={ctrl.updateDraft} />;
            case SendStep.Files:
                return (
                    <FilesStep
                        draft={draft}
                        rejected={state.rejectedFiles}
                        error={errors ? validateFilesStep(draft) : undefined}
                        onAdd={ctrl.handleAddFiles}
                        onRemove={ctrl.handleRemoveFile}
                        onDismissRejected={() => dispatch({ type: ACTION_TYPES.setRejectedFiles, payload: [] })}
                    />
                );
            case SendStep.Vehicles:
                return (
                    <VehiclesStep
                        draft={draft}
                        vehicles={state.vehicles}
                        loading={state.vehiclesLoading}
                        error={errors ? validateVehiclesStep(draft) : undefined}
                        onToggle={ctrl.toggleVehicle}
                        onSelectAll={ctrl.selectAllVehicles}
                    />
                );
            default:
                return <ReviewStep draft={draft} vehicles={state.vehicles} onEdit={ctrl.goToStep} />;
        }
    }

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Enviar remessa"
                    subtitle="Envie documentos para publicação nos veículos oficiais, um de cada vez ou vários em lote"
                    breadcrumbs={[{ label: 'Enviar remessa' }]}
                    actions={<Button variant="outlined" startIcon={<InboxIcon />} onClick={() => navigate('/remessas')}>Minhas remessas</Button>}
                />
            </Grid>

            <ClientGate
                isClientUser={client.isClientUser}
                clientId={client.clientId}
                onChange={client.setClientId}
                description="para carregar os veículos de publicação e enviar as remessas"
            >
                {state.result ? (
                    <Grid item xs={12}>
                        <SubmissionSuccess
                            result={state.result}
                            onViewSubmission={(id) => navigate(`/remessas?id=${id}`)}
                            onViewBatch={(batchId) => navigate(`/remessas?lote=${batchId}`)}
                            onNew={ctrl.startNew}
                        />
                    </Grid>
                ) : (
                    <React.Fragment>
                        <Grid item xs={12} lg={8}>
                            <Paper sx={{ p: { xs: 2, md: 3 } }}>
                                {state.drafts.length > 1 && (
                                    <Chip size="small" color="primary" variant="outlined" label={`Remessa ${draftIndex + 1} de ${state.drafts.length}`} sx={{ mb: 2 }} />
                                )}
                                <SendStepper
                                    draft={draft}
                                    activeStep={state.activeStep}
                                    reached={state.reached[draft.key] ?? SendStep.Data}
                                    showErrors={errors}
                                    onStep={ctrl.goToStep}
                                />
                                <Box key={`${draft.key}-${state.activeStep}`} sx={{ minHeight: 320 }}>
                                    {renderStep()}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Button variant="outlined" color="error" startIcon={<CloseIcon />} onClick={ctrl.discard} disabled={state.sending}>Cancelar</Button>
                                    <Box sx={{ flex: 1 }} />
                                    {state.activeStep > SendStep.Data && (
                                        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={ctrl.back} disabled={state.sending}>Voltar</Button>
                                    )}
                                    {state.activeStep < SendStep.Review ? (
                                        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={ctrl.next}>Continuar</Button>
                                    ) : (
                                        <LoadingButton variant="contained" startIcon={<SendIcon />} loadingPosition="start" loading={state.sending} onClick={ctrl.send}>
                                            {state.drafts.length === 1 ? 'Enviar remessa' : `Enviar ${state.drafts.length} remessas`}
                                        </LoadingButton>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <DraftList
                                drafts={state.drafts}
                                activeKey={state.activeKey}
                                sending={state.sending}
                                progress={state.progress}
                                generalErrors={state.generalErrors}
                                onSelect={ctrl.selectDraft}
                                onDuplicate={ctrl.duplicateDraft}
                                onRemove={ctrl.removeDraft}
                                onAdd={ctrl.addDraft}
                                onSend={ctrl.send}
                            />
                        </Grid>
                    </React.Fragment>
                )}
            </ClientGate>
        </React.Fragment>
    );
}
